import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { SmallCard } from 'components/SmallCard';
import RegularTable from 'components/RegularTable';
import { CustomDateFilter } from 'components/CustomDateFilter';
import {
  RecentActivity,
  RecentActivityBottom,
  RecentActivityClickable,
} from 'components/RecentActivity';
import { formatCurrencyAbbreviated, numberWithDot } from 'helpers/utils';
import { DashboardScreen, trackEvent } from 'helpers/mixpanelEvents';
import { ISummaryTotals } from 'store/summary/types';
import { IconNames } from 'components/Icon';
import { DateFilters } from 'components/Filters/DateFilters';
import { summaryPageColumns, summaryRecipientColumns } from 'constants/tables';
import { useCarousel } from 'hooks/useCarousel';
import { filterDateOptions } from 'constants/filters';
import { dimensions } from 'constants/dimensions';
import { useDashboardData } from 'hooks/useDashboardData';
import { GivingActivityChart } from './GivingActivityChart';

const SCREEN: DashboardScreen = 'donations_dashboard';

const DonationsDashboard = () => {
  const {
    donationState,
    transactionState,
    summaryState,
    totalDonation,
    totalTransaction,
    donationTrend,
    summary,
    donors,
    donorsLoading,
    donationDiff,
    transactionDiff,
    chartData,
    dimension,
    setDimension,
    recentActivityMini,
    setRecentActivityMini,
    showCustomHeader,
    setShowCustomHeader,
  } = useDashboardData();

  const handleDimensionChange = (newDimension: string) => {
    trackEvent('Summary Table Dimension Changed', { screen: SCREEN, dimension: newDimension });
    setDimension(newDimension);
  };

  const carouselComponents = [
    <div key="donation-cards" className="col-lg-4 col-md-8 col-xs-12">
      <SmallCard
        title="Raised Today"
        subtitle="Yesterday"
        titleValue={`$${numberWithDot(totalDonation?.today)}`}
        titleValueAbbreviated={formatCurrencyAbbreviated(totalDonation?.today)}
        subtitleValue={`$${numberWithDot(totalDonation?.yesterday)}`}
        subtitleValueAbbreviated={formatCurrencyAbbreviated(totalDonation?.yesterday)}
        badgeStyle={`${parseInt(donationDiff) > 0 ? 'blue' : 'orange'}-badge`}
        badgeValue={`${donationDiff} %`}
        icon={IconNames.MoneyBill}
        isLoading={donationState.isLoading}
      />
      <SmallCard
        title="Transactions Today"
        subtitle="Yesterday"
        titleValue={totalTransaction?.today?.toString() || '...'}
        subtitleValue={totalTransaction?.yesterday?.toString() || '...'}
        badgeStyle={`${parseInt(transactionDiff) > 0 ? 'blue' : 'orange'}-badge`}
        badgeValue={`${transactionDiff} %`}
        icon={IconNames.Arrows}
        isLoading={transactionState.isLoading}
      />
    </div>,
    <div key="giving-activity-chart" className="col-lg-8 col-md-8 col-xs-12">
      <GivingActivityChart
        isLoading={donationState.isLoading}
        donationTrend={donationTrend}
        chartData={chartData}
      />
    </div>,
    <div key="summary-table" className="col-md-12">
      <RegularTable<ISummaryTotals>
        title={`Summary (By ${dimension})`}
        subtitle="Listed in order of donation date"
        columns={dimension === 'page' ? summaryPageColumns : summaryRecipientColumns}
        data={summary?.filter(
          item =>
            !item?.recipient?.includes('Oath') && !item?.recipient?.includes('51C Collaborative')
        )}
        dimensionOnChange={handleDimensionChange}
        dimensionOptions={dimensions}
        dimensionValue={dimension}
        withPagination
        onPageChange={(direction, page) =>
          trackEvent('Summary Table Paginated', { screen: SCREEN, direction, page })
        }
        isLoading={summaryState.isLoading}
      />
    </div>,
  ];

  const {
    direction,
    itemCount,
    sliderVariants,
    sliderTransition,
    dragEndHandler,
    activeItemIndex,
    skipToItem,
  } = useCarousel({ carouselComponents });

  const previousActiveItemIndexRef = useRef(activeItemIndex);
  const carouselChangeMethodRef = useRef<'swipe' | 'dot_click'>('swipe');

  useEffect(() => {
    const previousActiveItemIndex = previousActiveItemIndexRef.current;
    if (previousActiveItemIndex === activeItemIndex) return;
    previousActiveItemIndexRef.current = activeItemIndex;
    trackEvent('Carousel Slide Changed', {
      screen: SCREEN,
      method: carouselChangeMethodRef.current,
      direction: activeItemIndex > previousActiveItemIndex ? 'next' : 'previous',
    });
  }, [activeItemIndex]);

  return (
    <>
      <div className="row filters-row">
        <DateFilters setIsCustom={setShowCustomHeader} labels={filterDateOptions} screen={SCREEN} />
        <RecentActivityClickable
          isMini={recentActivityMini}
          setIsMini={setRecentActivityMini}
          screen={SCREEN}
        />
      </div>

      <div className={`content ${recentActivityMini ? 'content-mini' : ''}`}>
        <RecentActivity isMini={recentActivityMini} donors={donors} isLoading={donorsLoading} />
        <div className="custom-header-container">
          {showCustomHeader && <CustomDateFilter centered screen={SCREEN} />}
        </div>
        <div className="hidden-on-desktop">
          <div className="carousel-container">
            <div className="carousel">
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={itemCount}
                  custom={direction}
                  variants={sliderVariants}
                  initial="incoming"
                  animate="active"
                  exit="exit"
                  transition={sliderTransition}
                  drag="x"
                  dragElastic={1}
                  onDragEnd={(_, dragInfo) => {
                    carouselChangeMethodRef.current = 'swipe';
                    dragEndHandler(dragInfo);
                  }}
                  className="motion-container"
                >
                  {carouselComponents[activeItemIndex]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="dots-row">
            {carouselComponents.map((_, index) => (
              <div
                key={index}
                className={classNames('dot', { active: index === activeItemIndex })}
                onClick={() => {
                  carouselChangeMethodRef.current = 'dot_click';
                  skipToItem(index);
                }}
              />
            ))}
          </div>
        </div>
        <div className="hidden-on-mobile">
          <div className="row">
            {carouselComponents.slice(0, -1).map((component, index) => (
              <React.Fragment key={index}>{component}</React.Fragment>
            ))}
          </div>
          <div className="row">{carouselComponents.pop()}</div>
        </div>
        <RecentActivityBottom
          isMini={recentActivityMini}
          setIsMini={setRecentActivityMini}
          donors={donors}
          isLoading={donorsLoading}
          screen={SCREEN}
        />
      </div>
    </>
  );
};

export default DonationsDashboard;
